// useJanetNoticings — the signed-in person's open "Janet noticed" items from
// the mismatch pass (janet_noticings, own rows only under RLS), plus the two
// rulings: look into it (Janet takes the question) and "that's fine". Both
// writes check the returned row, so a click that changed nothing never reads
// as success.
//
// Degrades quietly before migration 20261012b_janet_noticings.sql: `available`
// is false and Home shows nothing.

import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { MAX_NOTICINGS } from '../lib/janetNoticings'
import { visibleUnderFamilyGate, filterByFamilyGate } from '../lib/janetSourceScope'
import { familyEnabledInBrowser } from '../lib/janetFamilyGateClient'

const COLS = 'id, rank, kind, title, question, facts, absence, suggestion, deadline_at, deadline_label, topic, sources, status, run_at'

export async function fetchNoticing(id) {
  const { data, error } = await supabase.from('janet_noticings').select(COLS).eq('id', id).maybeSingle()
  if (error) return { noticing: null, error: error.message }
  const noticing = data && visibleUnderFamilyGate(data, familyEnabledInBrowser()) ? data : null
  return { noticing, error: noticing ? null : 'This could not be found.' }
}

export async function ruleOnNoticing(id, status, via = 'home') {
  if (!['acknowledged', 'accepted', 'dismissed'].includes(status)) return { ok: false, error: 'Unknown response' }
  const unavailable = { ok: false, unavailable: true, error: 'Acknowledgment is not available yet. Please try again after the update is complete.' }
  if (status === 'acknowledged') {
    const { error } = await supabase.from('janet_noticings').select('notification_state').limit(1)
    if (error) return unavailable
  }
  // A conversation can point to an earlier run's row. A response applies to
  // the same noticing across runs, including its current pending escalation.
  const { data: item, error: readError } = await supabase.from('janet_noticings')
    .select('user_id, fingerprint').eq('id', id).maybeSingle()
  if (readError || !item) return { ok: false, error: readError?.message || 'This could not be found.' }
  const at = new Date().toISOString()
  const { data, error } = await supabase.from('janet_noticings')
    .update({ status, acted_at: at, acted_via: via, updated_at: at })
    .eq('user_id', item.user_id).eq('fingerprint', item.fingerprint)
    .select('id')
  if (error) return status === 'acknowledged' && error.code === '23514' ? unavailable : { ok: false, error: error.message }
  if (!data?.length) return { ok: false, error: 'Nothing was changed — it may belong to someone else.' }
  return { ok: true }
}

export function useJanetNoticings(userId) {
  const [noticings, setNoticings] = useState([])
  const [loading, setLoading] = useState(true)
  const [available, setAvailable] = useState(true)

  const load = useCallback(async () => {
    if (!userId) { setNoticings([]); setLoading(false); return }
    const { data, error } = await supabase.from('janet_noticings')
      .select(COLS)
      .eq('user_id', userId)
      .eq('status', 'open')
      .order('run_at', { ascending: false })
      .order('rank', { ascending: true })
      .limit(MAX_NOTICINGS)
    if (error) {
      setAvailable(false)
      setNoticings([])
      console.info('[janet-noticings] not available yet:', error.message)
    } else {
      setAvailable(true)
      setNoticings(filterByFamilyGate(data, familyEnabledInBrowser()))
    }
    setLoading(false)
  }, [userId])

  useEffect(() => { load() }, [load])

  const dismiss = useCallback(async (id) => {
    const res = await ruleOnNoticing(id, 'dismissed', 'home')
    if (res.ok) setNoticings(prev => prev.filter(n => n.id !== id))
    return res
  }, [])

  return { noticings, loading, available, reload: load, dismiss }
}
